<template>
  <div>
    <div class="section-header">
      <h3>Credit Update</h3>
      <div class="section-header-right">
        <SelectButton
          class="p-selectbutton-xs"
          v-model="viewType"
          :options="[
            { label: 'Latest', value: 0 },
            { label: 'Archive', value: 1 }
          ]"
          option-value="value"
          option-label="label"
        />
        <a
          class="text-overline ml-2"
          :href="`https://summit.williamblair.com/#/company/${props.issuer}`"
          target="_blank"
        >
          Edit
        </a>
      </div>
    </div>
    <div class="content-card py-4 pr-2" :style="{ height: $vuetify.display.xs || expandedView ? 'auto' : '360px' }">
      <div v-if="loaded" style="height: 100%; overflow: auto">
        <div v-if="viewType === 0">
          <div v-if="latestCreditUpdate">
            <div class="credit-update-title" v-text="latestCreditUpdate.creditUpdateTitle"></div>
            <div class="byline">
              Created by&nbsp;
              <b>{{ latestCreditUpdate.createdByFullName }}</b>
              <span v-if="latestCreditUpdate.onBehalfOfFullName">
                on behalf of
                <b>{{ latestCreditUpdate.onBehalfOfFullName }}</b>
              </span>
              <span v-if="latestCreditUpdate.creditUpdateDate" class="update-date">
                &nbsp; {{ latestCreditUpdate.creditUpdateDate }} ago
              </span>
            </div>
            <!--eslint-disable-next-line vue/no-v-html-->
            <div class="body" v-html="latestCreditUpdate.creditUpdateBody"></div>
          </div>
          <Message v-else severity="info">{{ errorMessage }}</Message>
        </div>
        <div v-else>
          <div class="archive-list">
            <div v-for="item in archivedCreditUpdates" :key="item.id" class="archive-item" @click="item.visible = true">
              <div class="credit-update-title text-primary">
                {{ item.creditUpdateTitle }}
              </div>
              <div class="byline d-flex">
                created by&nbsp;
                <b>{{ item.createdByFullName }}</b>
                <span v-if="item.onBehalfOfFullName">
                  on behalf of
                  <b>{{ item.onBehalfOfFullName }}</b>
                </span>
                <div v-if="item.creditUpdateDate" class="update-date">&nbsp; {{ item.creditUpdateDate }} ago</div>
              </div>
              <Dialog
                modal
                dismissable-mask
                class="max-w-[60rem]"
                v-model:visible="item.visible"
                :header="item.creditUpdateTitle"
              >
                <div class="body text-base" v-html="item.creditUpdateBody"></div>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="process-indicator">
        <ProgressSpinner :style="{ width: '100px', height: '100px' }"></ProgressSpinner>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
interface Event {
  objectId: string
  title: string
  body: string
  createdBy: { fullName: string }
  onBehalfOf: { fullName: string }
  eventDate: Date
}

export interface CreditUpdate {
  id: string
  creditUpdateTitle: string
  creditUpdateBody: string
  creditUpdateDate: string | null
  createdByFullName: string | null
  onBehalfOfFullName: string | null
  visible: boolean
}
</script>

<script setup lang="ts">
import { ref, onMounted } from "vue"
import SelectButton from "primevue/selectbutton"
import Message from "primevue/message"
import Dialog from "primevue/dialog"
import ProgressSpinner from "primevue/progressspinner"

import { tryFormatDate } from "@/utils/dateHelper"
import { emdClient } from "@/utils/apiClients"

const props = defineProps({
  issuer: { type: String, required: true },
  expandedView: { type: String, required: false }
})

const loaded = ref(false)
const viewType = ref(0) // 0=latest, 1=archive
const latestCreditUpdate = ref<CreditUpdate | null>(null)
const errorMessage = ref("")
const archivedCreditUpdates = ref<CreditUpdate[]>([])

const getCreditUpdateFromEvent = (event: Event) => {
  const { objectId, title, body, createdBy, onBehalfOf, eventDate } = event
  return {
    id: objectId,
    creditUpdateTitle: title,
    creditUpdateBody: tidyCreditUpdate(body),
    creditUpdateDate: tryFormatDate(eventDate),
    createdByFullName: createdBy?.fullName,
    onBehalfOfFullName: onBehalfOf?.fullName,
    visible: false
  }
}

const tidyCreditUpdate = (body: string) => {
  const regex = /style\s*=\s*["'][^"']*["']/gm
  return body.replace(regex, "")
}

const getCreditUpdates = async (issuer: string) => {
  try {
    if (!emdClient) throw new Error("emdClient not initialized")
    const { status, data } = await emdClient.post(`/api/issuers/${issuer}/events`, {
      codes: ["EMERGING_MARKETS"],
      categoryCode: "COMMENTARY"
    })
    if (status !== 200) {
      errorMessage.value = "Unable to fetch emerging market commentory data from summit API"
    } else {
      if (data.events && data.events.length > 0) {
        const updates = data.events.map(getCreditUpdateFromEvent)
        latestCreditUpdate.value = updates[0]
        archivedCreditUpdates.value = updates.slice(0)
      }
    }
  } catch {
    errorMessage.value = "Unable to requerst emerging market commentory data from summit API"
  } finally {
    loaded.value = true
  }
}

onMounted(async () => {
  await getCreditUpdates(props.issuer)
})
</script>

<style>
.credit-update-title {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
  line-height: 1.5;
}
.byline {
  font-size: 12px;
  margin: 2px 0;
  color: #999;
}
.body {
  font-size: 13px;
}
.process-indicator {
  display: grid;
  min-height: 320px;
  align-items: center;
  justify-content: center;
}

.archive-item {
  padding: 8px;
  border-bottom: 1px solid #555557;
  cursor: pointer;
  border-radius: 4px;
}
</style>
